// Implements ADF rendering
// Docs: https://developer.atlassian.com/cloud/jira/platform/apis/document/structure/
// Schema: https://unpkg.com/@atlaskit/adf-schema@52.2.4/dist/json-schema/v1/full.json

import type { IncidentRichText, JiraWikiAttachmentReference } from '@/lib/jira-wiki';

interface AdfRenderContext {
    unsupportedNodeTypes: Set<string>;
    unsupportedMarkTypes: Set<string>;
    attachments: JiraWikiAttachmentReference[];
    inlineAttachmentIds: Set<string>;
    inlineAttachmentFileNames: string[];
}

const reportedUnsupportedAdfNodeTypes = new Set<string>();
const reportedUnsupportedAdfMarkTypes = new Set<string>();

type AdfRenderOptions = {
    attachments?: JiraWikiAttachmentReference[];
};

function createAdfRenderContext(options?: AdfRenderOptions): AdfRenderContext {
    return {
        unsupportedNodeTypes: new Set<string>(),
        unsupportedMarkTypes: new Set<string>(),
        attachments: options?.attachments ?? [],
        inlineAttachmentIds: new Set<string>(),
        inlineAttachmentFileNames: [],
    };
}

function recordUnsupportedAdfNodeType(type: unknown, context: AdfRenderContext): void {
    if (typeof type === 'string' && type) {
        context.unsupportedNodeTypes.add(type);
    }
}

function recordUnsupportedAdfMarkType(type: unknown, context: AdfRenderContext): void {
    if (typeof type === 'string' && type) {
        context.unsupportedMarkTypes.add(type);
    }
}

function reportUnsupportedAdfContent(context: AdfRenderContext): void {
    const nodeTypes = [...context.unsupportedNodeTypes].filter((type) => !reportedUnsupportedAdfNodeTypes.has(type));
    const markTypes = [...context.unsupportedMarkTypes].filter((type) => !reportedUnsupportedAdfMarkTypes.has(type));

    if (nodeTypes.length === 0 && markTypes.length === 0) return;

    nodeTypes.forEach((type) => reportedUnsupportedAdfNodeTypes.add(type));
    markTypes.forEach((type) => reportedUnsupportedAdfMarkTypes.add(type));

    console.warn('[incident-comments] Unsupported ADF content encountered', {
        nodeTypes,
        markTypes,
    });
}

function formatAdfDate(timestamp: unknown): string {
    if (typeof timestamp !== 'string' || timestamp.length === 0) return '';

    const parsedTimestamp = Number(timestamp);
    if (!Number.isFinite(parsedTimestamp)) return timestamp;

    const date = new Date(parsedTimestamp);
    if (Number.isNaN(date.getTime())) return timestamp;

    return date.toISOString().slice(0, 10);
}

function sanitizeHtmlHref(href: unknown): string | null {
    if (typeof href !== 'string' || href.length === 0) return null;

    return /^(https?:|mailto:|\/)/i.test(href) ? href : null;
}

function sanitizeHtmlColor(color: unknown): string | null {
    if (typeof color !== 'string') return null;

    return /^#[0-9a-fA-F]{6}$/.test(color) ? color.toLowerCase() : null;
}

function escapeHtml(text: string): string {
    return text
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#39;');
}

function normalizeAttachmentFileName(fileName: string): string {
    return fileName
        .trim()
        .replace(/\s\(([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\)(\.[^.]+)$/i, '$2');
}

function isImageAttachment(attachment: JiraWikiAttachmentReference): boolean {
    if (attachment.contentType?.startsWith('image/')) return true;

    const extension = attachment.fileName.split('.').pop()?.toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension ?? '');
}

function getAdfNodeAttrs(node: Record<string, unknown>): Record<string, unknown> {
    return node.attrs && typeof node.attrs === 'object' ? (node.attrs as Record<string, unknown>) : {};
}

function wrapAdfTextWithMark(text: string, mark: unknown, context: AdfRenderContext): string {
    if (!mark || typeof mark !== 'object') return text;

    const record = mark as Record<string, unknown>;

    switch (record.type) {
        case 'em':
            return `_${text}_`;
        case 'strong':
            return `*${text}*`;
        case 'underline':
            return `+${text}+`;
        case 'strike':
            return `-${text}-`;
        case 'textColor':
            return text;
        case 'code':
            return `{{${text}}}`;
        case 'link': {
            const href =
                record.attrs && typeof record.attrs === 'object' ? (record.attrs as Record<string, unknown>).href : '';
            return typeof href === 'string' && href ? `[${text}|${href}]` : text;
        }
        case 'subsup': {
            const type =
                record.attrs && typeof record.attrs === 'object'
                    ? (record.attrs as Record<string, unknown>).type
                    : undefined;
            if (type === 'sup') return `^${text}^`;
            if (type === 'sub') return `~${text}~`;
            return text;
        }
        default:
            recordUnsupportedAdfMarkType(record.type, context);
            return text;
    }
}

function applyAdfMarks(text: string, marks: unknown, context: AdfRenderContext): string {
    if (!Array.isArray(marks) || marks.length === 0) return text;

    return marks.reduce((currentText, mark) => wrapAdfTextWithMark(currentText, mark, context), text);
}

function renderAdfInlineNode(node: unknown, context: AdfRenderContext): string {
    if (!node || typeof node !== 'object') return '';

    const record = node as Record<string, unknown>;

    switch (record.type) {
        case 'text':
            return applyAdfMarks(typeof record.text === 'string' ? record.text : '', record.marks, context);
        case 'emoji': {
            const attrs =
                record.attrs && typeof record.attrs === 'object' ? (record.attrs as Record<string, unknown>) : {};
            if (typeof attrs.text === 'string' && attrs.text) return attrs.text;
            if (typeof attrs.shortName === 'string' && attrs.shortName) return attrs.shortName;
            return '';
        }
        case 'mention': {
            const attrs =
                record.attrs && typeof record.attrs === 'object' ? (record.attrs as Record<string, unknown>) : {};
            if (typeof attrs.text === 'string' && attrs.text) return attrs.text;
            if (typeof attrs.id === 'string' && attrs.id) return `@${attrs.id}`;
            return '';
        }
        case 'date': {
            const attrs =
                record.attrs && typeof record.attrs === 'object' ? (record.attrs as Record<string, unknown>) : {};
            return formatAdfDate(attrs.timestamp);
        }
        case 'inlineCard': {
            const attrs =
                record.attrs && typeof record.attrs === 'object' ? (record.attrs as Record<string, unknown>) : {};
            return typeof attrs.url === 'string' ? attrs.url : '';
        }
        case 'status': {
            const attrs =
                record.attrs && typeof record.attrs === 'object' ? (record.attrs as Record<string, unknown>) : {};
            return typeof attrs.text === 'string' && attrs.text ? `[${attrs.text}]` : '';
        }
        case 'hardBreak':
            return '\n';
        default:
            recordUnsupportedAdfNodeType(record.type, context);
            return Array.isArray(record.content)
                ? record.content.map((child) => renderAdfInlineNode(child, context)).join('')
                : '';
    }
}

function renderAdfInlineContent(content: unknown, context: AdfRenderContext): string {
    if (!Array.isArray(content)) return '';

    return content.map((child) => renderAdfInlineNode(child, context)).join('');
}

function renderAdfListItem(node: unknown, marker: string, depth: number, context: AdfRenderContext): string {
    if (!node || typeof node !== 'object') return '';

    const record = node as Record<string, unknown>;
    const children = Array.isArray(record.content) ? record.content : [];
    const indent = '  '.repeat(depth);
    const childIndent = `${indent}  `;
    const textBlocks: string[] = [];
    const nestedLists: string[] = [];

    for (const child of children) {
        if (!child || typeof child !== 'object') continue;

        const childRecord = child as Record<string, unknown>;

        if (childRecord.type === 'bulletList' || childRecord.type === 'orderedList') {
            const nested = renderAdfBlockNode(child, context, depth + 1);
            if (nested) nestedLists.push(nested);
            continue;
        }

        const renderedBlock = renderAdfBlockNode(child, context, depth).trim();
        if (renderedBlock) textBlocks.push(renderedBlock);
    }

    const lines = textBlocks.join('\n').split('\n');
    const formatted: string[] = [];

    if (lines[0]?.trim()) {
        formatted.push(`${indent}${marker} ${lines[0]}`);
        for (const line of lines.slice(1)) {
            formatted.push(line.trim() ? `${childIndent}${line}` : '');
        }
    } else {
        formatted.push(`${indent}${marker}`);
    }

    for (const nestedList of nestedLists) {
        if (formatted.at(-1) !== '') formatted.push('');
        formatted.push(nestedList);
    }

    return formatted.join('\n');
}

function renderAdfList(content: unknown, depth: number, ordered: boolean, context: AdfRenderContext): string {
    if (!Array.isArray(content)) return '';

    return content
        .map((child, index) => renderAdfListItem(child, ordered ? `${index + 1}.` : '*', depth, context))
        .filter((item) => item.trim().length > 0)
        .join('\n');
}

function renderAdfCodeBlock(node: Record<string, unknown>): string {
    const code = Array.isArray(node.content)
        ? node.content
              .map((child) => {
                  if (!child || typeof child !== 'object') return '';
                  const record = child as Record<string, unknown>;
                  return typeof record.text === 'string' ? record.text : '';
              })
              .join('')
        : '';

    if (!code.trim()) return '';

    const attrs = node.attrs && typeof node.attrs === 'object' ? (node.attrs as Record<string, unknown>) : {};
    const language = typeof attrs.language === 'string' && attrs.language ? attrs.language : '';

    return language ? `\`\`\`${language}\n${code}\n\`\`\`` : `\`\`\`\n${code}\n\`\`\``;
}

function wrapAdfHtmlWithMark(text: string, mark: unknown, context: AdfRenderContext): string {
    if (!mark || typeof mark !== 'object') return text;

    const record = mark as Record<string, unknown>;

    switch (record.type) {
        case 'em':
            return `<em>${text}</em>`;
        case 'strong':
            return `<strong>${text}</strong>`;
        case 'underline':
            return `<u>${text}</u>`;
        case 'strike':
            return `<s>${text}</s>`;
        case 'textColor': {
            const color =
                record.attrs && typeof record.attrs === 'object' ? (record.attrs as Record<string, unknown>).color : '';
            const safeColor = sanitizeHtmlColor(color);
            return safeColor ? `<span style="color:${safeColor}">${text}</span>` : text;
        }
        case 'code':
            return `<code>${text}</code>`;
        case 'link': {
            const href =
                record.attrs && typeof record.attrs === 'object' ? (record.attrs as Record<string, unknown>).href : '';
            const safeHref = sanitizeHtmlHref(href);
            return safeHref
                ? `<a href="${escapeHtml(safeHref)}" target="_blank" rel="noopener noreferrer">${text}</a>`
                : text;
        }
        case 'subsup': {
            const type =
                record.attrs && typeof record.attrs === 'object'
                    ? (record.attrs as Record<string, unknown>).type
                    : undefined;
            if (type === 'sup') return `<sup>${text}</sup>`;
            if (type === 'sub') return `<sub>${text}</sub>`;
            return text;
        }
        default:
            recordUnsupportedAdfMarkType(record.type, context);
            return text;
    }
}

function applyAdfHtmlMarks(text: string, marks: unknown, context: AdfRenderContext): string {
    if (!Array.isArray(marks) || marks.length === 0) return text;

    return marks.reduce((currentText, mark) => wrapAdfHtmlWithMark(currentText, mark, context), text);
}

function renderAdfInlineNodeAsHtml(node: unknown, context: AdfRenderContext): string {
    if (!node || typeof node !== 'object') return '';

    const record = node as Record<string, unknown>;

    switch (record.type) {
        case 'text':
            return applyAdfHtmlMarks(
                escapeHtml(typeof record.text === 'string' ? record.text : ''),
                record.marks,
                context,
            );
        case 'emoji': {
            const attrs =
                record.attrs && typeof record.attrs === 'object' ? (record.attrs as Record<string, unknown>) : {};
            if (typeof attrs.text === 'string' && attrs.text) return escapeHtml(attrs.text);
            if (typeof attrs.shortName === 'string' && attrs.shortName) return escapeHtml(attrs.shortName);
            return '';
        }
        case 'mention': {
            const attrs =
                record.attrs && typeof record.attrs === 'object' ? (record.attrs as Record<string, unknown>) : {};
            if (typeof attrs.text === 'string' && attrs.text) return escapeHtml(attrs.text);
            if (typeof attrs.id === 'string' && attrs.id) return escapeHtml(`@${attrs.id}`);
            return '';
        }
        case 'date': {
            const attrs =
                record.attrs && typeof record.attrs === 'object' ? (record.attrs as Record<string, unknown>) : {};
            return escapeHtml(formatAdfDate(attrs.timestamp));
        }
        case 'inlineCard': {
            const attrs =
                record.attrs && typeof record.attrs === 'object' ? (record.attrs as Record<string, unknown>) : {};
            const safeHref = sanitizeHtmlHref(attrs.url);
            return safeHref
                ? `<a href="${escapeHtml(safeHref)}" target="_blank" rel="noopener noreferrer">${escapeHtml(safeHref)}</a>`
                : '';
        }
        case 'status': {
            const attrs =
                record.attrs && typeof record.attrs === 'object' ? (record.attrs as Record<string, unknown>) : {};
            return typeof attrs.text === 'string' && attrs.text ? `<span>${escapeHtml(attrs.text)}</span>` : '';
        }
        case 'hardBreak':
            return '<br>';
        default:
            recordUnsupportedAdfNodeType(record.type, context);
            return Array.isArray(record.content)
                ? record.content.map((child) => renderAdfInlineNodeAsHtml(child, context)).join('')
                : '';
    }
}

function renderAdfInlineContentAsHtml(content: unknown, context: AdfRenderContext): string {
    if (!Array.isArray(content)) return '';

    return content.map((child) => renderAdfInlineNodeAsHtml(child, context)).join('');
}

function renderAdfListItemAsHtml(node: unknown, context: AdfRenderContext): string {
    if (!node || typeof node !== 'object') return '';

    const record = node as Record<string, unknown>;
    const children = Array.isArray(record.content) ? record.content : [];
    const parts: string[] = [];

    for (const child of children) {
        if (!child || typeof child !== 'object') continue;

        const childRecord = child as Record<string, unknown>;
        if (childRecord.type === 'paragraph') {
            const paragraphHtml = renderAdfInlineContentAsHtml(childRecord.content, context).trim();
            if (paragraphHtml) parts.push(paragraphHtml);
            continue;
        }

        const blockHtml = renderAdfBlockNodeAsHtml(child, context);
        if (blockHtml) parts.push(blockHtml);
    }

    return parts.length > 0 ? `<li>${parts.join('')}</li>` : '';
}

function renderAdfListAsHtml(content: unknown, context: AdfRenderContext, ordered: boolean): string {
    if (!Array.isArray(content)) return '';

    const tag = ordered ? 'ol' : 'ul';
    const items = content
        .map((child) => renderAdfListItemAsHtml(child, context))
        .filter((item) => item.length > 0)
        .join('');

    return items ? `<${tag}>${items}</${tag}>` : '';
}

function renderAdfCodeBlockAsHtml(node: Record<string, unknown>): string {
    const code = Array.isArray(node.content)
        ? node.content
              .map((child) => {
                  if (!child || typeof child !== 'object') return '';
                  const record = child as Record<string, unknown>;
                  return typeof record.text === 'string' ? record.text : '';
              })
              .join('')
        : '';

    if (!code.trim()) return '';

    return `<pre><code>${escapeHtml(code)}</code></pre>`;
}

function findAdfMediaNode(node: unknown): Record<string, unknown> | null {
    if (!node || typeof node !== 'object') return null;

    const record = node as Record<string, unknown>;
    if (record.type === 'media') return record;

    if (!Array.isArray(record.content)) return null;

    for (const child of record.content) {
        const mediaNode = findAdfMediaNode(child);
        if (mediaNode) return mediaNode;
    }

    return null;
}

function resolveAdfMediaAttachment(attrs: Record<string, unknown>, context: AdfRenderContext) {
    const mediaId = typeof attrs.id === 'string' ? attrs.id.trim() : '';
    const alt = typeof attrs.alt === 'string' ? normalizeAttachmentFileName(attrs.alt) : '';
    const normalizedAlt = alt.toLowerCase();
    const imageAttachments = context.attachments.filter(isImageAttachment);

    const matchedById = mediaId
        ? imageAttachments.find((attachment) => String(attachment.id) === mediaId)
        : undefined;
    if (matchedById) return matchedById;

    const matchedByAlt = normalizedAlt
        ? imageAttachments.find((attachment) => normalizeAttachmentFileName(attachment.fileName).toLowerCase() === normalizedAlt)
        : undefined;
    if (matchedByAlt) return matchedByAlt;

    if (imageAttachments.length === 1) return imageAttachments[0];

    return mediaId
        ? {
              id: mediaId,
              fileName: alt || 'Inline attachment',
              contentType: 'image/*',
          }
        : null;
}

function renderAdfMediaAsHtml(node: unknown, context: AdfRenderContext): string {
    const mediaNode = findAdfMediaNode(node);
    if (!mediaNode) return '';

    const attrs = getAdfNodeAttrs(mediaNode);
    const attachment = resolveAdfMediaAttachment(attrs, context);
    if (!attachment?.id) return '';

    const attachmentId = String(attachment.id);
    const normalizedFileName = normalizeAttachmentFileName(attachment.fileName);
    const width = typeof attrs.width === 'number' ? String(Math.round(attrs.width)) : undefined;
    const height = typeof attrs.height === 'number' ? String(Math.round(attrs.height)) : undefined;
    const sizeAttribute = width ?? height;
    const widthAttribute = sizeAttribute ? ` data-inline-width="${escapeHtml(sizeAttribute)}"` : '';

    context.inlineAttachmentIds.add(attachmentId);
    context.inlineAttachmentFileNames.push(normalizedFileName.toLowerCase());

    return `<figure class="comment-inline-image" data-inline-attachment-id="${escapeHtml(attachmentId)}" data-inline-alt="${escapeHtml(normalizedFileName)}"${widthAttribute}></figure>`;
}

function renderAdfTableCellInnerHtml(content: unknown, context: AdfRenderContext): string {
    if (!Array.isArray(content)) return '';

    return content
        .map((child) => {
            if (!child || typeof child !== 'object') return '';

            const record = child as Record<string, unknown>;
            if (record.type === 'paragraph') {
                return renderAdfInlineContentAsHtml(record.content, context).trim();
            }

            return renderAdfBlockNodeAsHtml(child, context);
        })
        .filter((part) => part.length > 0)
        .join('');
}

function renderAdfTableRowAsHtml(node: unknown, context: AdfRenderContext): string {
    if (!node || typeof node !== 'object') return '';

    const record = node as Record<string, unknown>;
    const cells = Array.isArray(record.content)
        ? record.content
              .map((child) => {
                  if (!child || typeof child !== 'object') return '';

                  const cellRecord = child as Record<string, unknown>;
                  const tag = cellRecord.type === 'tableHeader' ? 'th' : cellRecord.type === 'tableCell' ? 'td' : '';
                  if (!tag) {
                      recordUnsupportedAdfNodeType(cellRecord.type, context);
                      return '';
                  }

                  const cellHtml = renderAdfTableCellInnerHtml(cellRecord.content, context);
                  return cellHtml ? `<${tag}>${cellHtml}</${tag}>` : '';
              })
              .filter((cell) => cell.length > 0)
              .join('')
        : '';

    return cells ? `<tr>${cells}</tr>` : '';
}

function renderAdfTableAsHtml(content: unknown, context: AdfRenderContext): string {
    if (!Array.isArray(content)) return '';

    const rows = content
        .map((child) => {
            if (!child || typeof child !== 'object') return '';
            const rowRecord = child as Record<string, unknown>;
            if (rowRecord.type !== 'tableRow') {
                recordUnsupportedAdfNodeType(rowRecord.type, context);
                return '';
            }

            return renderAdfTableRowAsHtml(child, context);
        })
        .filter((row) => row.length > 0)
        .join('');

    return rows ? `<table><tbody>${rows}</tbody></table>` : '';
}

function renderAdfTaskItemAsHtml(node: unknown, context: AdfRenderContext): string {
    if (!node || typeof node !== 'object') return '';

    const record = node as Record<string, unknown>;
    const attrs = record.attrs && typeof record.attrs === 'object' ? (record.attrs as Record<string, unknown>) : {};
    const checked = attrs.state === 'DONE';
    const contentHtml = Array.isArray(record.content)
        ? record.content
              .map((child) => {
                  if (!child || typeof child !== 'object') return '';

                  const childRecord = child as Record<string, unknown>;
                  if (childRecord.type === 'paragraph') {
                      return renderAdfInlineContentAsHtml(childRecord.content, context).trim();
                  }

                  return renderAdfBlockNodeAsHtml(child, context);
              })
              .filter((part) => part.length > 0)
              .join('')
        : '';

    if (!contentHtml) return '';

    return `<li><label><input type="checkbox"${checked ? ' checked' : ''} disabled> ${contentHtml}</label></li>`;
}

function renderAdfTaskListAsHtml(content: unknown, context: AdfRenderContext): string {
    if (!Array.isArray(content)) return '';

    const items = content
        .map((child) => {
            if (!child || typeof child !== 'object') return '';
            const itemRecord = child as Record<string, unknown>;
            if (itemRecord.type !== 'taskItem') {
                recordUnsupportedAdfNodeType(itemRecord.type, context);
                return '';
            }

            return renderAdfTaskItemAsHtml(child, context);
        })
        .filter((item) => item.length > 0)
        .join('');

    return items ? `<ul class="comment-task-list">${items}</ul>` : '';
}

function renderAdfChildBlocksAsHtml(content: unknown, context: AdfRenderContext): string {
    if (!Array.isArray(content)) return '';

    return content
        .map((child) => renderAdfBlockNodeAsHtml(child, context))
        .filter((part) => part.length > 0)
        .join('');
}

function resolveAdfPanelType(node: Record<string, unknown>): 'info' | 'note' | 'warning' | 'error' | 'success' {
    const attrs = node.attrs && typeof node.attrs === 'object' ? (node.attrs as Record<string, unknown>) : {};
    const panelType = typeof attrs.panelType === 'string' ? attrs.panelType : 'info';

    switch (panelType) {
        case 'note':
        case 'warning':
        case 'error':
        case 'success':
            return panelType;
        default:
            return 'info';
    }
}

function renderAdfPanelAsHtml(node: Record<string, unknown>, context: AdfRenderContext): string {
    const contentHtml = renderAdfChildBlocksAsHtml(node.content, context);
    if (!contentHtml) return '';

    const panelType = resolveAdfPanelType(node);
    const panelIcon = {
        info: 'i',
        note: '!',
        warning: '!',
        error: 'x',
        success: '+',
    }[panelType];

    return `<div class="adf-panel adf-panel--${panelType}" data-panel-type="${panelType}"><span class="adf-panel__icon" aria-hidden="true">${panelIcon}</span><div class="adf-panel__content">${contentHtml}</div></div>`;
}

function renderAdfBlockNodeAsHtml(node: unknown, context: AdfRenderContext): string {
    if (!node || typeof node !== 'object') return '';

    const record = node as Record<string, unknown>;

    switch (record.type) {
        case 'doc':
            return renderAdfChildBlocksAsHtml(record.content, context);
        case 'paragraph': {
            const contentHtml = renderAdfInlineContentAsHtml(record.content, context).trim();
            return contentHtml ? `<p>${contentHtml}</p>` : '';
        }
        case 'heading': {
            const level =
                record.attrs &&
                typeof record.attrs === 'object' &&
                typeof (record.attrs as Record<string, unknown>).level === 'number'
                    ? Math.min(Math.max((record.attrs as Record<string, unknown>).level as number, 1), 6)
                    : 1;
            const contentHtml = renderAdfInlineContentAsHtml(record.content, context).trim();
            return contentHtml ? `<h${level}>${contentHtml}</h${level}>` : '';
        }
        case 'bulletList':
            return renderAdfListAsHtml(record.content, context, false);
        case 'orderedList':
            return renderAdfListAsHtml(record.content, context, true);
        case 'taskList':
            return renderAdfTaskListAsHtml(record.content, context);
        case 'taskItem':
            return renderAdfTaskItemAsHtml(node, context);
        case 'listItem':
            return renderAdfListItemAsHtml(node, context);
        case 'codeBlock':
            return renderAdfCodeBlockAsHtml(record);
        case 'table':
            return renderAdfTableAsHtml(record.content, context);
        case 'tableRow':
            return renderAdfTableRowAsHtml(node, context);
        case 'tableHeader':
            return renderAdfTableCellInnerHtml(record.content, context);
        case 'tableCell':
            return renderAdfTableCellInnerHtml(record.content, context);
        case 'blockquote': {
            const contentHtml = renderAdfChildBlocksAsHtml(record.content, context);
            return contentHtml ? `<blockquote class="adf-blockquote">${contentHtml}</blockquote>` : '';
        }
        case 'panel':
            return renderAdfPanelAsHtml(record, context);
        case 'expand': {
            return renderAdfChildBlocksAsHtml(record.content, context);
        }
        case 'media':
        case 'mediaSingle':
        case 'mediaGroup':
            return renderAdfMediaAsHtml(record, context);
        case 'rule':
            return '<hr class="adf-rule">';
        default: {
            recordUnsupportedAdfNodeType(record.type, context);
            const contentHtml = renderAdfChildBlocksAsHtml(record.content, context);

            if (contentHtml) return contentHtml;

            const inlineHtml = renderAdfInlineContentAsHtml(record.content, context).trim();
            return inlineHtml ? `<p>${inlineHtml}</p>` : '';
        }
    }
}

function renderAdfBlockNode(node: unknown, context: AdfRenderContext, depth = 0): string {
    if (!node || typeof node !== 'object') return '';

    const record = node as Record<string, unknown>;

    switch (record.type) {
        case 'doc':
            return Array.isArray(record.content)
                ? record.content
                      .map((child) => renderAdfBlockNode(child, context, depth))
                      .filter((part) => part.trim().length > 0)
                      .join('\n\n')
                : '';
        case 'paragraph':
        case 'heading':
            return renderAdfInlineContent(record.content, context).trim();
        case 'codeBlock':
            return renderAdfCodeBlock(record);
        case 'bulletList':
            return renderAdfList(record.content, depth, false, context);
        case 'orderedList':
            return renderAdfList(record.content, depth, true, context);
        case 'listItem':
            return renderAdfListItem(node, '*', depth, context);
        case 'blockquote':
        case 'panel':
        case 'expand':
            return Array.isArray(record.content)
                ? record.content
                      .map((child) => renderAdfBlockNode(child, context, depth))
                      .filter((part) => part.trim().length > 0)
                      .join('\n\n')
                : '';
        case 'media':
        case 'mediaSingle':
        case 'mediaGroup':
        case 'rule':
            return '';
        default: {
            recordUnsupportedAdfNodeType(record.type, context);

            const inlineText = renderAdfInlineContent(record.content, context).trim();
            if (inlineText) return inlineText;

            return Array.isArray(record.content)
                ? record.content
                      .map((child) => renderAdfBlockNode(child, context, depth))
                      .filter((part) => part.trim().length > 0)
                      .join('\n\n')
                : '';
        }
    }
}

export function isAdfDocument(node: unknown): node is { type: 'doc'; version: number; content: unknown[] } {
    if (!node || typeof node !== 'object') return false;

    const record = node as Record<string, unknown>;

    return record.type === 'doc' && typeof record.version === 'number' && Array.isArray(record.content);
}

export function parseAdfDocumentString(
    text: string | null | undefined,
): { type: 'doc'; version: number; content: unknown[] } | null {
    if (!text) return null;

    const trimmedText = text.trim();
    if (!trimmedText.startsWith('{')) return null;

    try {
        const parsed = JSON.parse(trimmedText);
        return isAdfDocument(parsed) ? parsed : null;
    } catch {
        return null;
    }
}

export function renderAdfDocumentToText(node: unknown): string {
    if (!isAdfDocument(node)) return '';

    const context = createAdfRenderContext();
    const adfText = renderAdfBlockNode(node, context).trim();
    reportUnsupportedAdfContent(context);
    return adfText;
}

export function resolveAdfRichTextValue(value: unknown, options?: AdfRenderOptions): IncidentRichText | null {
    const parsed = isAdfDocument(value) ? value : typeof value === 'string' ? parseAdfDocumentString(value) : null;
    if (!parsed) return null;

    const context = createAdfRenderContext(options);
    const adfHtml = renderAdfBlockNodeAsHtml(parsed, context).trim();
    reportUnsupportedAdfContent(context);

    return adfHtml
        ? {
              text: adfHtml,
              renderAsHtml: true,
              ...(context.inlineAttachmentIds.size > 0 ? { inlineAttachmentIds: [...context.inlineAttachmentIds] } : {}),
              ...(context.inlineAttachmentFileNames.length > 0
                  ? { inlineAttachmentFileNames: context.inlineAttachmentFileNames }
                  : {}),
          }
        : {
              text: '',
              renderAsHtml: false,
          };
}

export function resolveAdfRichText(text: string | null | undefined, options?: AdfRenderOptions): IncidentRichText | null {
    return resolveAdfRichTextValue(text, options);
}
